import React, {memo} from 'react';
import MUIBox from '@/components/MUI-Components/MUI-Box';
import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import DisplayImage from '../display-image';
import {colors, fontFamily, fontWeight, fontSize, spacing} from '@/styles/tokens';

// ─── sx objects — module-level constants ──────────────────────────────────────
const challengeSubHeadingSx = {
    fontFamily: fontFamily.body,
    fontSize: fontSize[24],
    fontWeight: fontWeight.regular,
    textAlign: 'center',
    lineHeight: '36px',
    '@media (max-width:430px)': {fontSize: fontSize[18], textAlign: 'left', lineHeight: '30px'}
} as const;

const clientWrapperSx = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[20],
    flexWrap: 'wrap',
    '@media (max-width:430px)': {justifyContent: 'center'}
} as const;

const challengeFooterHeadingSx = {
    fontFamily: fontFamily.heading,
    fontSize: fontSize[24],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    '& span': {color: '#337361'},
    '@media (max-width:430px)': {fontSize: '26px', lineHeight: '36px', marginTop: '10px'}
} as const;

const challengeListHeadingSx = {
    fontFamily: fontFamily.heading,
    fontWeight: fontWeight.regular,
    fontSize: fontSize[24],
    '@media (max-width:430px)': {fontSize: fontSize[18], textAlign: 'left', lineHeight: '30px'}
} as const;

// className retained on the JSX node below: `.everledger-main-section .challenge-list-wrapper .challenge-list span`
// (case-study-everledger page override) still targets this literal class chain — left in globals.scss, untouched.
const challengeListSx = {
    fontFamily: fontFamily.body,
    fontSize: fontSize[24],
    fontWeight: fontWeight.regular,
    display: 'list-item',
    marginTop: spacing[20],
    lineHeight: '36px',
    '& span': {fontFamily: fontFamily.heading, fontSize: fontSize[24], fontWeight: fontWeight.bold},
    '@media (max-width:430px)': {
        fontSize: fontSize[18],
        textAlign: 'left',
        lineHeight: '30px',
        '& span': {fontSize: fontSize[18]}
    }
} as const;

// className retained: `.everledger-main-section .challenge-list-wrapper` (list-style/text-align) page override
// still targets this class — left in globals.scss, untouched.
const challengeListWrapperSx = {
    '@media (max-width:768px)': {py: 0, px: spacing[20]}
} as const;

const challengeFooterImgStyle = {width: '100%', height: 'auto', objectFit: 'cover'} as const;

const Challenge = (props: any) => {
    const {
        heading,
        subheading,
        footerimg,
        clientDescription,
        className,
        image,
        challengelistData,
        clientproductimg,
        mapheading,
        clientname,
        clientintro,
        subheadingClass,
        challengelistDataHeading,
        imgClassName,
        headingSx,
        subheadingSx
    } = props;
    return (
        <MUIBox className="challenge-main-box">
            <MUITypography variant="h2" className={`challenge-heading ${className} margin center`} sx={headingSx}>
                {heading}
            </MUITypography>
            <MUIBox className="divder-wrapper">
                <hr className="divider-line top-spacing" />
            </MUIBox>
            {subheading !== '' && (
                <MUIBox className="sub-heading-box">
                    <MUITypography className={`${subheadingClass} color-black`} sx={{...challengeSubHeadingSx, ...subheadingSx}}>
                        {subheading}
                    </MUITypography>
                </MUIBox>
            )}
            <MUIGrid container className="top-spacing " spacing={5}>
                {clientDescription ? (
                    <MUIGrid item xs={12} sm={12} md={6} lg={6}>
                        <MUITypography className="client-description">{clientDescription}</MUITypography>
                    </MUIGrid>
                ) : null}

                {image && clientproductimg ? (
                    <MUIGrid item xs={12} sm={12} md={6} lg={6}>
                        <MUIBox sx={clientWrapperSx}>
                            <DisplayImage src={image} alt="client" width={192} height={192} />
                            <MUIBox>
                                <MUITypography className="client-name">{clientname}</MUITypography>
                                <MUITypography className="client-intro">{clientintro}</MUITypography>
                                <DisplayImage
                                    src={clientproductimg}
                                    alt="client"
                                    className="helper-card-icon-client"
                                    // width={120}
                                    // height={41}
                                />
                            </MUIBox>
                        </MUIBox>
                    </MUIGrid>
                ) : null}
            </MUIGrid>

            {mapheading ? (
                <MUITypography className="margin" sx={challengeFooterHeadingSx}>
                    {mapheading}
                </MUITypography>
            ) : null}
            {challengelistDataHeading && <MUITypography sx={challengeListHeadingSx}>{challengelistDataHeading}</MUITypography>}
            {challengelistData?.map((data: any, index: number) => (
                <MUIBox key={index} className="challenge-list-wrapper" sx={challengeListWrapperSx}>
                    <MUITypography className="challenge-list" sx={challengeListSx}>
                        {data.text}
                    </MUITypography>
                </MUIBox>
            ))}
            <MUIBox className="top-space">
                {footerimg && (
                    <DisplayImage
                        src={footerimg}
                        alt="client"
                        width={1128}
                        height={700}
                        sizes="100vw"
                        quality={100}
                        className={`${imgClassName}`}
                        style={challengeFooterImgStyle}
                    />
                )}
            </MUIBox>
        </MUIBox>
    );
};
export default memo(Challenge);
